// User permission configuration for PVS Forms and Documents.
//
// Drives per-user visibility of individual forms/documents in the UI.
// Admins (isAdmin=true), 'gareth_holton' (mirrors gareth), and readonly
// users always have full visibility. Standard users use a deny-list that
// hides specific items; unknown standard users are denied by default.
//
// Doc keys here must match:
//   - keys in DOC_REGISTRY / GROUPS inside src/components/Documents.js
//   - currentForm values inside src/components/PVSForms.js

const ALL_DOC_KEYS = [
  // Documents menu
  'wse',
  'hsafety',
  'ImpartialityConfidentialityDoc',
  'VesselTaggingPolicyDoc',
  'SiteLayoutsDoc',
  'liftingtransport',
  'vesselprep',
  'ForkLiftTruckDoc',
  'vesselairtest',
  'ManualHandlingDoc',
  'PortableHandToolsDoc',
  'FireGeneralSafetyDoc',
  'pryolysis',
  'shotblasting',
  'spraybooth',
  'COSHH',
  'JetWashPressureWasherDoc',
  'PillarDrillDoc',
  'WeldingDoc',
  'BenchGrinderDoc',
  'GuillotineDoc',
  'WorkplaceTransportDoc',
  'DrivingGoodsVehiclesDoc',
  'DriverThirdPartySitesDoc',
  'LoneWorkingDoc',
  'OvergroundTankBasesDoc',
  'DeGassingFlareDoc',
  'AccessingTrailersDoc',
  'FLTChangingLPGCylindersDoc',
  'LaddersStepladdersDoc',
  'OvensDoc',
  'PortableGasSpaceHeatersDoc',
  'SlingsChainsLiftingDoc',
  'OnSiteRA',
  'PortableCompressorUnitDoc',
  'VesselRefurbishmentProcessMap',
  // PVS Forms
  'tank-movement',
  'tank-certification',
  'final-qc-checklist',
  'engineering',
  'pre-inspection',
  'paint-quality-inspection',
  'pre-coating-batch',
  'vessel-failure-report',
  'customer-complaint',
  'ultrasonic-daily-check',
  'bump-test',
  'gas-free-check',
  'cg-scrap-delivery-note',
];

// Users with full access regardless of explicit rules.
// gareth_holton is listed here because it should mirror gareth.
const FULL_ACCESS_USERS = new Set([
  'gareth',
  'gareth_holton',
  'office',
  'readonlypvs',
]);

const FULL_ACCESS_ROLES = new Set(['admin', 'system_admin', 'readonly']);

// Keys hidden from each user. Anything not listed here is visible.
// Derived from the master USERPERMISSION matrix (Apr 2026).
const DENY_LISTS = {
  adybates: [
    'liftingtransport',
    'vesselprep',
    'tank-movement',
    'tank-certification',
    'final-qc-checklist',
    'pre-inspection',
    'vessel-failure-report',
    'customer-complaint',
    'vesselairtest',
    'PillarDrillDoc',
    'WeldingDoc',
    'BenchGrinderDoc',
    'GuillotineDoc',
    'WorkplaceTransportDoc',
    'DrivingGoodsVehiclesDoc',
    'DriverThirdPartySitesDoc',
    'OvergroundTankBasesDoc',
    'AccessingTrailersDoc',
    'OnSiteRA',
    'engineering',
    'PortableCompressorUnitDoc',
  ],
  spencerwright: [
    'vesselprep',
    'tank-movement',
    'tank-certification',
    'final-qc-checklist',
    'pre-inspection',
    'vessel-failure-report',
    'customer-complaint',
    'vesselairtest',
    'spraybooth',
    'PillarDrillDoc',
    'WeldingDoc',
    'DrivingGoodsVehiclesDoc',
    'DriverThirdPartySitesDoc',
    'OnSiteRA',
    'engineering',
    'paint-quality-inspection',
    'pre-coating-batch',
    'PortableCompressorUnitDoc',
  ],
  driver: [
    'vesselprep',
    'tank-certification',
    'final-qc-checklist',
    'pre-inspection',
    'vessel-failure-report',
    'customer-complaint',
    'vesselairtest',
    'shotblasting',
    'spraybooth',
    'WeldingDoc',
    'BenchGrinderDoc',
    'GuillotineDoc',
    'LoneWorkingDoc',
    'OvensDoc',
    'engineering',
    'paint-quality-inspection',
    'pre-coating-batch',
    'PortableCompressorUnitDoc',
  ],
  kevinwalker: [
    'WeldingDoc',
    'DrivingGoodsVehiclesDoc',
    'DriverThirdPartySitesDoc',
    'LoneWorkingDoc',
    'OvensDoc',
    'paint-quality-inspection',
    'pre-coating-batch',
    'customer-complaint',
  ],
  shartley: [
    'WeldingDoc',
    'DrivingGoodsVehiclesDoc',
    'DriverThirdPartySitesDoc',
    'LoneWorkingDoc',
    'OvensDoc',
    'paint-quality-inspection',
    'pre-coating-batch',
    'customer-complaint',
  ],
  ethanbrunning: [
    'WeldingDoc',
    'DrivingGoodsVehiclesDoc',
    'DriverThirdPartySitesDoc',
    'LoneWorkingDoc',
    'OvensDoc',
    'paint-quality-inspection',
    'pre-coating-batch',
    'customer-complaint',
  ],
  adybrunning: [
    'WeldingDoc',
    'DrivingGoodsVehiclesDoc',
    'DriverThirdPartySitesDoc',
    'LoneWorkingDoc',
    'OvensDoc',
    'paint-quality-inspection',
    'pre-coating-batch',
    'customer-complaint',
  ],
  pvsengineer: [
    'WeldingDoc',
    'DrivingGoodsVehiclesDoc',
    'DriverThirdPartySitesDoc',
    'LoneWorkingDoc',
    'OvensDoc',
    'paint-quality-inspection',
    'pre-coating-batch',
    'customer-complaint',
  ],
};

function getStoredUser() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem('user');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function canAccess(docKey, userOverride = null) {
  const user = userOverride || getStoredUser();
  if (!user) return false;

  if (user.isAdmin === true) return true;

  const role = user.role || 'standard';
  if (FULL_ACCESS_ROLES.has(role)) return true;

  const username = (user.username || '').toLowerCase();
  if (FULL_ACCESS_USERS.has(username)) return true;

  const deny = DENY_LISTS[username];
  if (!deny) return false;
  return !deny.includes(docKey);
}

export function filterAccessibleItems(items, getKey = (item) => item.key) {
  return items.filter((item) => canAccess(getKey(item)));
}

export const PERMISSION_DOC_KEYS = ALL_DOC_KEYS;
